import { IQuestionTag, ITagData, ITag, Tag } from '@/models/configurator';
import { Question } from '@/models/configurator/question';
import { QuestionService } from '@/services/configurator/question';
import { ActionTree, GetterTree, MutationTree } from 'vuex';

export type QuestionState = typeof state;

const questionService = new QuestionService();

export const state = {
  questions: [] as Question[],
  tags: [] as ITagData[]
};

export const mutations: MutationTree<QuestionState> = {
  ADD_QUESTION(state, payload: Question) {
    state.questions.push(payload);
  },

  SET_QUESTIONS(state, payload: Question[]) {
    state.questions = payload;
  },

  UPDATE_QUESTION(state, payload: Question) {
    const index = state.questions.findIndex(
      question => question.id === payload.id
    );
    state.questions.splice(index, 1, payload);
  },

  REMOVE_QUESTION(state, payload: Question) {
    const index = state.questions.indexOf(payload);
    state.questions.splice(index, 1);
  },

  SET_TAGS(state, payload: ITagData[]) {
    state.tags = payload;
  },

  CREATE_OR_UPDATE_TAG(state, payload: ITagData) {
    const index = state.tags.findIndex(tag => tag.id === payload.id);
    if (index === -1) {
      state.tags.push(payload);
    } else {
      state.tags.splice(index, 1, payload);
    }
  },

  REMOVE_TAG(state, payload: number) {
    const index = state.tags.findIndex(tag => tag.id === payload);
    if (index !== -1) {
      state.tags.splice(index, 1);
    }
  }
};

export const actions: ActionTree<QuestionState, QuestionState> = {
  createQuestion: async ({ commit, state }, question: Question) =>
    await new Promise((resolve, reject) =>
      questionService
        .createQuestion(question)
        .then(question => {
          const index = state.questions.findIndex(s => s.id === question.id);
          if (index === -1) {
            commit('ADD_QUESTION', question);
          } else {
            commit('UPDATE_QUESTION', question);
          }
          resolve(question);
        })
        .catch(err => {
          reject(err);
        })
    ),

  fetchQuestions: (
    { commit },
    { module, configuration }: { module: number; configuration?: number }
  ) =>
    new Promise((resolve, reject) => {
      questionService
        .fetchQuestions(module, configuration)
        .then(questions => {
          commit('SET_QUESTIONS', questions);
          resolve(questions);
        })
        .catch(err => {
          reject(err);
        });
    }),

  fetchQuestion: (
    { commit, state },
    {
      module,
      id,
      configuration
    }: { module: number; id: number; configuration?: number }
  ) =>
    new Promise((resolve, reject) => {
      questionService
        .fetchQuestion(module, id, configuration)
        .then(question => {
          const index = state.questions.findIndex(s => s.id === question.id);
          if (index === -1) {
            commit('ADD_QUESTION', question);
          } else {
            commit('UPDATE_QUESTION', question);
          }
          resolve(question);
        })
        .catch(err => {
          reject(err);
        });
    }),

  updateQuestion: ({ commit, state }, question: Question) =>
    new Promise((resolve, reject) => {
      questionService
        .updateQuestion(question)
        .then(question => {
          const index = state.questions.findIndex(s => s.id === question.id);
          if (index === -1) {
            commit('ADD_QUESTION', question);
          } else {
            commit('UPDATE_QUESTION', question);
          }
          resolve(question);
        })
        .catch(err => {
          reject(err);
        });
    }),

  deleteQuestion: ({ commit }, question: Question) =>
    new Promise((resolve, reject) => {
      questionService
        .deleteQuestion(question)
        .then(() => {
          commit('REMOVE_QUESTION', question);
        })
        .catch(err => {
          reject(err);
        });
    }),

  updateQuestionOrder: (
    { commit, state },
    { module, id, order }: { module: number; id: number; order: number }
  ) =>
    new Promise((resolve, reject) => {
      questionService
        .updateQuestionOrder(module, id, order)
        .then(question => {
          const index = state.questions.findIndex(s => s.id === question.id);
          if (index === -1) {
            commit('ADD_QUESTION', question);
          } else {
            commit('UPDATE_QUESTION', question);
          }
          resolve(question);
        })
        .catch(err => {
          reject(err);
        });
    }),

  addQuestionTag: (
    { commit, state },
    { module, id, tag }: { module: number; id: number; tag: IQuestionTag }
  ) => {
    return questionService.addQuestionTag(module, id, tag).then(question => {
      const index = state.questions.findIndex(s => s.id === question.id);
      if (index === -1) {
        commit('ADD_QUESTION', question);
      } else {
        commit('UPDATE_QUESTION', question);
      }
    });
  },

  removeQuestionTag: (
    { commit, state },
    { module, id, tag }: { module: number; id: number; tag: number }
  ) => {
    return questionService.removeQuestionTag(module, id, tag).then(question => {
      const index = state.questions.findIndex(s => s.id === question.id);
      if (index === -1) {
        commit('ADD_QUESTION', question);
      } else {
        commit('UPDATE_QUESTION', question);
      }
    });
  },

  fetchTags: ({ commit }) =>
    questionService.fetchTags().then(tags => {
      commit('SET_TAGS', tags);
    }),

  createTag: ({ commit }, payload: ITagData) =>
    questionService.createTag(payload).then(tag => {
      commit('CREATE_OR_UPDATE_TAG', tag);
    }),

  updateTag: ({ commit }, payload: ITagData) =>
    questionService.updateTag(payload).then(tag => {
      commit('CREATE_OR_UPDATE_TAG', tag);
    }),

  deleteTag: ({ commit }, payload: number) =>
    questionService.deleteTag(payload).then(() => {
      commit('REMOVE_TAG', payload);
    })
};

export const getters: GetterTree<QuestionState, QuestionState> = {
  getQuestionById: state => (module: number | string, id: number | string) => {
    return state.questions.find(
      (question: any) => question.module === module && question.id === id
    );
  },

  getQuestionWithModuleId: state => (module: number) => {
    return state.questions.filter(
      (question: any) => question.module === module
    );
  },

  sortedQuestions: state => state.questions.sort((a, b) => a.order! - b.order!),

  getTags: (state: QuestionState): ITag[] =>
    state.tags.map(tag => new Tag(tag)),

  getTagById: (state: QuestionState) => (id: number): ITag | undefined => {
    const data = state.tags.find(tag => tag.id === id);
    if (!data) {
      return undefined;
    }

    return new Tag(data);
  }
};
